import React, { useState, useEffect, useCallback } from 'react';
import '../../styles/ExerciseLayout.css';
import QuestionLearnButton from '../full_mock_test/attempt/QuestionLearnButton';

const OPTION_LETTERS = ['A', 'B', 'C', 'D'];

const KetReadingPart5 = ({ exerciseData, attemptId, exerciseId, showHint, isReviewMode, fillHintSignal }) => {
  const [answers, setAnswers] = useState({});
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    if (attemptId && exerciseId) {
      // 1. Prioritize API data in review mode
      if (isReviewMode && exerciseData.questions) {
        const migratedAnswers = {};
        exerciseData.questions.forEach(q => {
          if (q.user_answer && typeof q.user_answer.answer === 'string') {
            migratedAnswers[q.id] = q.user_answer.answer;
          }
        });
        if (Object.keys(migratedAnswers).length > 0) {
          setAnswers(migratedAnswers);
          setIsHydrated(true);
          return;
        }
      }

      // 2. Otherwise load from localStorage
      const attemptKey = `attempt_${attemptId}`;
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) {
        try {
          const allAnswers = JSON.parse(savedAttempt);
          const savedData = allAnswers[exerciseId];
          if (savedData && typeof savedData === 'object') {
            const migratedAnswers = {};
            Object.keys(savedData).forEach(qId => {
              const value = savedData[qId];
              migratedAnswers[qId] = (typeof value === 'object' && value !== null && 'answer' in value) ? value.answer : value;
            });
            setAnswers(migratedAnswers);
          }
        } catch (e) {
          console.error("Failed to parse saved attempt from localStorage", e);
        }
      }
      setIsHydrated(true);
    }
  }, [attemptId, exerciseId, isReviewMode, exerciseData.questions]);

  const saveAnswersToLocalStorage = useCallback((currentAnswers) => {
    if (!isHydrated || !attemptId || !exerciseId || isReviewMode) return;

    const attemptKey = `attempt_${attemptId}`;
    let allAnswers = {};
    try {
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) allAnswers = JSON.parse(savedAttempt) || {};
    } catch (e) {
      allAnswers = {};
    }

    const answersToSave = {};
    Object.keys(currentAnswers).forEach((qId) => {
      answersToSave[qId] = { answer: currentAnswers[qId] };
    });

    allAnswers[exerciseId] = answersToSave;
    localStorage.setItem(attemptKey, JSON.stringify(allAnswers));
  }, [attemptId, exerciseId, isHydrated, isReviewMode]);

  useEffect(() => {
    if (fillHintSignal > 0) {
      const newAnswers = {};
      exerciseData.questions.forEach(q => {
        if (q.accepted_answers && q.accepted_answers.length > 0) {
          newAnswers[q.id] = q.accepted_answers[0];
        }
      });
      setAnswers(newAnswers);
      saveAnswersToLocalStorage(newAnswers);
    }
  }, [fillHintSignal, exerciseData.questions, saveAnswersToLocalStorage]);

  useEffect(() => {
    if (!isHydrated) return;
    const debounceSave = setTimeout(() => {
      saveAnswersToLocalStorage(answers);
    }, 500);
    return () => {
      clearTimeout(debounceSave);
      saveAnswersToLocalStorage(answers);
    };
  }, [answers, isHydrated, saveAnswersToLocalStorage]);

  if (!exerciseData || !exerciseData.content_data || !exerciseData.questions) {
    return <div>Loading exercise data...</div>;
  }

  const { story_title, reading_text } = exerciseData.content_data;

  const questions = [...exerciseData.questions].sort((a, b) => a.order_index - b.order_index);

  // accepted_answers holds the LETTER (A/B/C) — options are plain word strings
  // in A,B,C order. Store the letter so grading matches accepted_answers.
  const optionKeyOf = (option, idx) => (typeof option === 'string'
    ? (OPTION_LETTERS[idx] || String(idx))
    : (option.id || OPTION_LETTERS[idx]));
  const optionValueOf = (option) => (typeof option === 'string' ? option : (option.option_text || option.text || option.id));
  // Resolve an accepted LETTER back to its word for display (example gap, hints).
  const wordForAnswer = (question) => {
    const letter = (question.accepted_answers || [])[0];
    const idx = OPTION_LETTERS.indexOf(letter);
    const opt = (question.options || [])[idx];
    return opt != null ? optionValueOf(opt) : letter;
  };

  const handleSelect = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const findQuestionForToken = (qNumber, tokenText) => {
    return questions.find(q => q.order_index === qNumber)
      || questions.find(q => (q.question_text || '').trim() === tokenText);
  };

  const renderStory = () => {
    const parts = reading_text.split(/(\{\{\d+\}\})/g);
    return parts.map((part, index) => {
      const match = part.match(/\{\{(\d+)\}\}/);
      if (match) {
        const qNumber = parseInt(match[1], 10);
        const question = findQuestionForToken(qNumber, part);

        if (!question) return part;

        if (question.points === 0) {
          return <span key={index} className="exercise-example-text mx-1 font-bold text-primary-blue underline decoration-2 underline-offset-4">{wordForAnswer(question)}</span>;
        }

        const isAnswerCorrect = question.is_correct === true || question.is_correct === 'true';
        const selectedValue = (showHint || (isReviewMode && isAnswerCorrect))
          ? question.accepted_answers[0]
          : answers[question.id];

        return (
          <span key={index} className="inline-flex items-center gap-1 mx-1 align-middle flex-wrap">
            <strong className="exercise-gap-number align-middle">({qNumber})</strong>
            {(question.options || []).map((option, optIdx) => {
              const key = optionKeyOf(option, optIdx);
              const value = optionValueOf(option);
              const letter = OPTION_LETTERS[optIdx] || '';
              const isSelected = selectedValue === key;
              const isThisCorrect = question.accepted_answers?.includes(key);
              const isIncorrect = isReviewMode && !isAnswerCorrect && answers[question.id] === key && !isThisCorrect;

              return (
                <span
                  key={optIdx}
                  onClick={() => !isReviewMode && !showHint && isHydrated && handleSelect(question.id, key)}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg border-2 text-sm font-bold transition-all align-middle
                    ${isReviewMode || showHint ? 'cursor-default' : 'cursor-pointer'}
                    ${isSelected && !isReviewMode && !showHint ? 'border-primary-blue bg-blue-50 text-primary-blue' : ''}
                    ${isSelected && showHint ? 'border-amber-400 bg-amber-50 text-amber-700' : ''}
                    ${isReviewMode && isThisCorrect ? 'border-green-500 bg-green-50/50 text-green-700' : ''}
                    ${isIncorrect ? 'border-red-400 bg-red-50/50 text-red-500 line-through' : ''}
                    ${!isSelected && !isIncorrect && !(isReviewMode && isThisCorrect) ? 'border-slate-100 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-slate-300' : ''}
                  `}
                >
                  <span className="text-[10px] font-black opacity-60">{letter}</span>
                  {value}
                </span>
              );
            })}
            {isReviewMode && (isAnswerCorrect
              ? <span className="correct-checkmark align-middle scale-75">✓</span>
              : <span className="incorrect-checkmark align-middle scale-75">✗</span>
            )}
            {isReviewMode && (
              <span className="ml-1 align-middle inline-flex"><QuestionLearnButton question={question} size={18} /></span>
            )}
          </span>
        );
      }

      return part;
    });
  };

  return (
    <div className="w-full h-full flex flex-col p-4 md:px-10 overflow-y-auto custom-scrollbar">
      <div className="flex-1 flex flex-col min-h-0">
        <div className="exercise-story-box !mt-0 p-6 md:p-8 shadow-sm border border-slate-100 rounded-3xl bg-white dark:bg-slate-800/40 max-w-3xl mx-auto w-full">
          {story_title && (
            <div className="text-center w-full">
              <h3 className="exercise-story-title mb-6 text-2xl md:text-3xl font-display font-black text-slate-800 dark:text-white leading-tight">
                {story_title.replace(/\(Example\)/gi, '').trim()}
              </h3>
            </div>
          )}
          <div className="space-y-4 exercise-body-text text-base md:text-lg leading-loose text-slate-700 dark:text-slate-300 text-left">
            {renderStory()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default KetReadingPart5;
